// src/features/wf2/components/W2Card.jsx
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { getW2ById, W2_LOCAL_EVENT } from "../api";
import "./W2Card.css";
import { toEpochInTZ } from "../../../utils/datesBR";

const W2_META_DEBUG =
  (process.env.REACT_APP_W2_META_DEBUG || "0") === "1" ||
  (typeof window !== "undefined" && window.localStorage?.getItem("w2.meta.debug") === "1");

function dlog(...args) {
  if (W2_META_DEBUG) console.log("[W2Card/meta]", ...args);
}

const TZ = "America/Sao_Paulo";
const low = (v) => (v == null ? "" : String(v).trim().toLowerCase());

function isMissing(v) {
  return v == null || String(v).trim() === "";
}

function toHHMM(raw) {
  if (raw == null) return "";
  const txt = String(raw).trim();
  if (!txt) return "";

  let m = txt.match(/^(\d{1,3}):([0-5]\d)$/);
  if (m) return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;

  m = txt.match(/^(\d{1,3}):([0-5]\d):([0-5]\d)$/);
  if (m) return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;

  return txt;
}

function normalizeStatusW2(raw) {
  const s = low(raw);

  if (s === "em_validacao_automatica") return "validacao";
  if (s === "programado") return "programado";
  if (s === "editado") return "editado";
  if (s === "negado") return "negado";
  if (s === "reprogramar") return "reprogramar";
  if (s === "finalizado") return "finalizado";

  if (s === "executado") return "finalizado";
  if (s === "primeira_previa" || s === "segunda_previa") return "editado";
  if (s === "em_andamento" || s === "" || s == null) return "andamento";

  return "andamento";
}

function statusLabelW2(statusNorm) {
  if (statusNorm === "validacao") return "Pré-programação";
  if (statusNorm === "programado") return "Programado";
  if (statusNorm === "editado") return "Editado";
  if (statusNorm === "negado") return "Negado";
  if (statusNorm === "reprogramar") return "Reprogramar";
  if (statusNorm === "finalizado") return "Finalizado";
  if (statusNorm === "andamento") return "Não iniciado";
  return "";
}

function statusToClassW2(statusNorm) {
  if (statusNorm === "validacao") return "status-validacao";
  if (statusNorm === "programado") return "status-aceita";
  if (statusNorm === "editado") return "status-alterado";
  if (statusNorm === "negado") return "status-negada";
  if (statusNorm === "reprogramar") return "status-reprogramar";
  if (statusNorm === "finalizado") return "status-finalizado";
  return "status-default";
}

/* Parsing pre_meta_expira_em (proteção UTC) */
function ymdFromAny(v) {
  if (!v) return null;

  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];

  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(v));
  } catch {
    return null;
  }
}

function toPartsFromStringNoTZ(raw, refDayYMD) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;

  if (/^\d{10}$/.test(s)) return { kind: "epoch", ms: Number(s) * 1000 };
  if (/^\d{13}$/.test(s)) return { kind: "epoch", ms: Number(s) };

  s = s.replace(/\.\d+/, "");
  s = s.replace(/([Zz]|[+-]\d{2}:\d{2}|[+-]\d{4})$/, "");
  s = s.replace(" ", "T");

  let m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (m) {
    return { kind: "datetime", ymd: m[1], hh: Number(m[2]), mm: Number(m[3]), ss: Number(m[4] || "00") };
  }

  m = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (m) return { kind: "datetime", ymd: m[1], hh: 0, mm: 0, ss: 0 };

  m = s.match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (m) {
    const ymd = refDayYMD || new Date().toISOString().slice(0, 10);
    return { kind: "datetime", ymd, hh: Number(m[1]), mm: Number(m[2]), ss: Number(m[3] || "00") };
  }

  return null;
}

function msFromWallClockSP(parts) {
  if (!parts) return NaN;
  if (parts.kind === "epoch") return parts.ms;

  const [Y, M, D] = String(parts.ymd).split("-").map((x) => Number(x));
  if (!Y || !M || !D) return NaN;

  // interpreta hh:mm:ss como horário de São Paulo (UTC-3 fixo) e converte para epoch
  return new Date(Date.UTC(Y, M - 1, D, parts.hh, parts.mm, parts.ss, 0) + 3 * 3600 * 1000).getTime();
}

function msFromInstant(raw) {
  if (!raw) return NaN;

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.getTime();

  const s = String(raw).trim();
  if (!s) return NaN;

  if (/^\d{10}$/.test(s)) return Number(s) * 1000;
  if (/^\d{13}$/.test(s)) return Number(s);

  if (/[Zz]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(s)) {
    const t = new Date(s).getTime();
    return Number.isNaN(t) ? NaN : t;
  }

  return NaN;
}

function expiraMsSafe(raw) {
  return toEpochInTZ(raw, "America/Sao_Paulo");
}

function pad2(n) {
  return String(Math.max(0, Math.trunc(n))).padStart(2, "0");
}

function countdownHHMMSSFromMs(expMs, nowMs) {
  if (!Number.isFinite(expMs)) return "00:00:00";
  const diff = expMs - nowMs;
  if (diff <= 0) return "00:00:00";
  const totalSec = Math.max(1, Math.ceil(diff / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

function smartMerge(prev, incoming) {
  const a = prev || {};
  const b = incoming || {};
  const out = { ...a, ...b };

  const keepIfMissing = ["horario_do_intervalo", "tempo_do_intervalo", "pre_meta_expira_em"];
  for (const k of keepIfMissing) {
    if (isMissing(b?.[k]) && !isMissing(a?.[k])) out[k] = a[k];
  }
  return out;
}

function mergeCardState(prev, incoming) {
  const merged = smartMerge(prev, incoming);
  const prevObj = prev || {};
  const keys = new Set([...Object.keys(prevObj), ...Object.keys(merged || {})]);

  for (const key of keys) {
    if (prevObj[key] !== merged[key]) return merged;
  }
  return prevObj;
}

const W2Card = memo(function W2Card({ data = {}, isDragging = false, onClick }) {
  const [pulse, setPulse] = useState(false);
  const pulseTimerRef = useRef(null);

  const [row, setRow] = useState(data || {});

  useEffect(() => {
    setRow((prev) => mergeCardState(prev, data));
  }, [data]);

  const sig = useMemo(() => {
    const r = row || {};
    return [
      r.codigo_unico,
      r.id,
      r.data,
      r.status_wf2,
      r.pre_meta,
      r.meta,
      r.meta_versao,
      r.gerencia,
      r.coordenador,
      r.atividade,
      r.ativo,
      r.os,
      r.kmInicio,
      r.kmFinal,
      r.horario_do_intervalo,
      r.tempo_do_intervalo,
      r.pre_meta_expira_em,
    ]
      .map((v) => String(v ?? ""))
      .join("||");
  }, [
    row?.codigo_unico,
    row?.id,
    row?.data,
    row?.status_wf2,
    row?.pre_meta,
    row?.meta,
    row?.meta_versao,
    row?.gerencia,
    row?.coordenador,
    row?.atividade,
    row?.ativo,
    row?.os,
    row?.kmInicio,
    row?.kmFinal,
    row?.horario_do_intervalo,
    row?.tempo_do_intervalo,
    row?.pre_meta_expira_em,
  ]);

  const prevSigRef = useRef(sig);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevSigRef.current = sig;
      return;
    }
    if (prevSigRef.current !== sig) {
      prevSigRef.current = sig;
      setPulse(true);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => setPulse(false), 1200);
    }
  }, [sig]);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = null;
    };
  }, []);

  const rawStatus = row.status_wf2 ?? "";
  const statusNorm = normalizeStatusW2(rawStatus);

  const isPreProgramacao = statusNorm === "validacao";
  const statusClass = statusToClassW2(statusNorm);

  /* HIDRATAÇÃO */
  const hydratedRef = useRef(false);
  const lastIdRef = useRef(null);

  useEffect(() => {
    const id = row?.id ?? data?.id ?? null;
    if (!id) return;

    if (id !== lastIdRef.current) {
      lastIdRef.current = id;
      hydratedRef.current = false;
    }
    if (hydratedRef.current) return;

    const shouldHaveInterval =
      isPreProgramacao || statusNorm === "programado" || statusNorm === "editado" || statusNorm === "finalizado";

    const faltaHorario = shouldHaveInterval && isMissing(row?.horario_do_intervalo);
    const faltaTempo = shouldHaveInterval && isMissing(row?.tempo_do_intervalo);
    const faltaExpira = isPreProgramacao && isMissing(row?.pre_meta_expira_em);

    if (!(faltaHorario || faltaTempo || faltaExpira)) return;

    hydratedRef.current = true;

    getW2ById(id)
      .then((fresh) => {
        if (!fresh) return;
        setRow((prev) => mergeCardState(prev, fresh));
        window.dispatchEvent(new CustomEvent(W2_LOCAL_EVENT, { detail: { kind: "hydrate", w2: fresh } }));
      })
      .catch(() => {
        hydratedRef.current = false;
      });
  }, [
    row?.id,
    data?.id,
    row?.horario_do_intervalo,
    row?.tempo_do_intervalo,
    row?.pre_meta_expira_em,
    isPreProgramacao,
    statusNorm,
  ]);

  const kmText = row.kmInicio != null && row.kmFinal != null ? `${row.kmInicio} - ${row.kmFinal}` : "—";
  const horarioIntervalo = toHHMM(row.horario_do_intervalo || "");
  const tempoIntervalo = toHHMM(row.tempo_do_intervalo || "");

  const osRaw = row.os;
  const showOS = osRaw != null && String(osRaw).trim() !== "";

  const codRaw = row.codigo_unico != null ? String(row.codigo_unico).trim() : "";
  const showCodigo = codRaw !== "";

  /* Countdown */
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refDayYMD = useMemo(() => {
    return ymdFromAny(row?.data ?? row?.data_programacao ?? data?.data ?? data?.data_programacao) || null;
  }, [row?.data, row?.data_programacao, data?.data, data?.data_programacao]);

  const expMs = useMemo(() => {
    const epochA = Number(row?.pre_meta_expira_em_epoch ?? NaN);
    const epochB = Number(data?.pre_meta_expira_em_epoch ?? NaN);
    const parsedA = Number.isFinite(epochA) ? epochA : expiraMsSafe(row?.pre_meta_expira_em ?? null, refDayYMD);
    const parsedB = Number.isFinite(epochB) ? epochB : expiraMsSafe(data?.pre_meta_expira_em ?? null, refDayYMD);
    const arr = [parsedA, parsedB].filter((x) => Number.isFinite(x));
    return arr.length ? Math.max(...arr) : NaN;
  }, [row?.pre_meta_expira_em, row?.pre_meta_expira_em_epoch, data?.pre_meta_expira_em, data?.pre_meta_expira_em_epoch, refDayYMD]);

  useEffect(() => {
    if (!isPreProgramacao || !Number.isFinite(expMs)) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isPreProgramacao, expMs]);

  const showMetaBox = isPreProgramacao && Number.isFinite(expMs);
  const preMetaCountdown = showMetaBox ? countdownHHMMSSFromMs(expMs, nowMs) : "00:00:00";

  useEffect(() => {
    if (!W2_META_DEBUG) return;
    dlog("debug", {
      id: row?.id ?? data?.id ?? null,
      codigo_unico: row?.codigo_unico ?? data?.codigo_unico ?? null,
      refDayYMD,
      expMs,
      nowMs,
      preMetaCountdown,
      showMetaBox,
      statusNorm,
    });
  }, [row?.id, data?.id, row?.codigo_unico, data?.codigo_unico, refDayYMD, expMs, nowMs, preMetaCountdown, showMetaBox, statusNorm]);

  /* ========================================================
   * AUTO-META: Agora é processado automaticamente pelo backend
   * via cron a cada 10 segundos. O frontend apenas exibe o countdown
   * e recebe a atualização via WebSocket quando o backend converte.
   *
   * Código antigo removido para evitar deadlock entre frontend
   * e backend tentando processar o mesmo registro simultaneamente.
   * ======================================================== */

  const ger = (row.gerencia && String(row.gerencia).toUpperCase()) || "—";
  const ativo = row.ativo ? String(row.ativo).toUpperCase() : "";
  const coord = (row.coordenador && String(row.coordenador).toUpperCase()) || "";
  const ati = (row.atividade && String(row.atividade).toUpperCase()) || "";

  // ✅ evita duplicar "PRÉ-PROGRAMAÇÃO" (meta box já mostra)
  const footerLabel = showMetaBox ? "" : statusLabelW2(statusNorm);

  return (
    <div
      className={`w2card ${statusClass} ${showMetaBox ? "has-meta" : ""} ${isDragging ? "is-dragging" : ""} ${pulse ? "rt-pulse" : ""}`}
      onClick={onClick}
      style={pulse ? { boxShadow: "0 0 0 3px rgba(80,140,255,0.35)" } : undefined}
    >
      {/* topo (código centralizado) */}
      <div className="w2card-top">
        {showCodigo ? (
          <div className="w2card-codePill">{codRaw}</div>
        ) : (
          <div className="w2card-codePill w2card-codePill--empty">—</div>
        )}
      </div>

      {/* textos */}
      <div className="w2card-main">
        <div className="w2card-gerencia">{ger}</div>
        {ativo ? <div className="w2card-ativo">{ativo}</div> : null}

        {ati ? <div className="w2card-atividade">{ati}</div> : null}

        {coord ? <div className="w2card-coord">{coord}</div> : null}

        {/* ✅ OS entre coordenador e KM (ou linha invisível pra padronizar) */}
        {showOS ? (
          <div className="w2card-osLine">OS: {String(osRaw).trim()}</div>
        ) : (
          <div className="w2card-osLine w2card-osLine--ghost">&nbsp;</div>
        )}
      </div>

      {/* KM */}
      <div className="w2card-kmBox">
        <div className="w2card-kmTitle">KM</div>
        <div className="w2card-kmValue">{kmText}</div>
      </div>

      {/* Horários */}
      <div className="w2card-row2">
        <div className="w2card-mini">
          <div className="w2card-miniTitle">HOR. INTER.</div>
          <div className="w2card-miniValue">{horarioIntervalo || "—"}</div>
        </div>
        <div className="w2card-mini">
          <div className="w2card-miniTitle">TEMP. INTER.</div>
          <div className="w2card-miniValue">{tempoIntervalo || "—"}</div>
        </div>
      </div>

      {/* pré-programação */}
      {showMetaBox ? (
        <div className="w2card-metaBox">
          <div className="w2card-metaTitle">PRÉ-PROGRAMAÇÃO</div>
          <div className="w2card-metaRow">
            <div className="w2card-metaSub">
              Validação<br />automática
            </div>
            <div className="w2card-metaCountdown">{preMetaCountdown}</div>
          </div>
        </div>
      ) : null}

      {/* spacer pra manter tudo “pra cima” e ainda assim dar respiro */}
      <div className="w2card-spacer" />

      {/* status no final (exceto quando tem meta box) */}
      {footerLabel ? (
        <div className="w2card-footer">
          <div className="w2card-statusPill">{footerLabel}</div>
        </div>
      ) : null}
    </div>
  );
});

export default W2Card;
